import type { ReactNode } from "react";
import { Sidebar } from "./Sidebar";
import { FocusModeProvider, useFocusMode } from "./focus-mode";
import { cn } from "@/lib/utils";

interface AppLayoutProps {
  children: ReactNode;
}

/**
 * Root authenticated shell: persistent sidebar + a single content card
 * that hosts the active page. The card itself owns padding so pages can
 * render edge-to-edge without competing with the shell.
 *
 * Pages that want a chrome-less view can call `useFocusMode().setFocused(true)`
 * — the sidebar then hides and the outer gutter collapses.
 *
 * Mirrors `voicebot-ui-redesign/project/src/app.jsx` outer layout.
 */
export function AppLayout({ children }: Readonly<AppLayoutProps>) {
  return (
    <FocusModeProvider>
      <AppLayoutInner>{children}</AppLayoutInner>
    </FocusModeProvider>
  );
}

function AppLayoutInner({ children }: Readonly<AppLayoutProps>) {
  const { focused } = useFocusMode();
  return (
    <div
      className={cn(
        "flex h-screen w-screen box-border bg-background",
        focused ? "gap-0 p-0" : "gap-3 p-3",
      )}
    >
      {!focused && <Sidebar />}
      <main
        className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-card",
          focused
            ? "rounded-none border-0 p-3 shadow-none"
            : "rounded-lg border border-border p-5 shadow-sm",
        )}
      >
        {children}
      </main>
    </div>
  );
}
